import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputBase,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Loader from "../../common/Loader";
import { axiosInstance } from "../../../utils/axiosInstance";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "80vh",
    padding: "2rem",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
    gap: "1rem",
  },
  title: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#333",
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    padding: "0.4rem 0.8rem",
    width: "260px",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.95rem",
    color: "#333",
  },
  tableContainer: {
    marginTop: "1rem",
  },
  tableHead: {
    backgroundColor: "#f7f7f7",
  },
  tableCellHead: {
    fontWeight: 600,
    color: "#444",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
  },
  approved: {
    color: "#fff",
    backgroundColor: "#25B273",
    borderRadius: "10px",
    padding: "5px 15px",
    textTransform: "capitalize",
    "&:hover": {
      backgroundColor: "#1f995f",
    },
  },
  declined: {
    color: "#fff",
    backgroundColor: "#E6072C",
    borderRadius: "10px",
    padding: "5px 15px",
    textTransform: "capitalize",
    "&:hover": {
      backgroundColor: "#c50523",
    },
  },
  pending: {
    color: "#ffb300",
    textTransform: "capitalize",
  },
  statusBadge: {
    textTransform: "capitalize",
    padding: "4px 10px",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  approvedStatus: {
    backgroundColor: "#e6f9ef",
    color: "#25B273",
  },
  declinedStatus: {
    backgroundColor: "#fdecec",
    color: "#f44336",
  },
  noData: {
    textAlign: "center",
    padding: "2rem 0",
    color: "#888",
  },
}));

const EnrolleeDependent = () => {
  const classes = useStyles();

  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchDependents = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/v1/admin/dependants`);
        setDependents(res?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch dependents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDependents();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      setUpdatingId(id);
      await axiosInstance.patch(`/v1/admin/dependants/${id}/status`, {
        status,
      });
      setDependents((prev) =>
        prev.map((dep) => (dep.id === id ? { ...dep, status } : dep))
      );
    } catch (error) {
      console.error("Failed to update dependent status:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredDependents = useMemo(() => {
    if (!searchTerm.trim()) return dependents;
    return dependents.filter((dep) => {
      const fullName = `${dep.first_name || ""} ${
        dep.last_name || ""
      }`.toLowerCase();
      return (
        fullName.includes(searchTerm.toLowerCase()) ||
        (dep.gender || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dep.status || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [dependents, searchTerm]);

  if (loading) return <Loader visible />;

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Manage Dependents</Typography>

        {/* Search bar */}
        <div className={classes.searchContainer}>
          <SearchIcon color="action" />
          <InputBase
            placeholder="Search dependents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={classes.searchInput}
          />
        </div>
      </div>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCellHead}>Name</TableCell>
              <TableCell className={classes.tableCellHead}>Gender</TableCell>
              <TableCell className={classes.tableCellHead}>
                Date of Birth
              </TableCell>
              <TableCell className={classes.tableCellHead}>Status</TableCell>
              <TableCell className={classes.tableCellHead}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredDependents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.noData}>
                  No dependents found
                </TableCell>
              </TableRow>
            ) : (
              filteredDependents.map((dep) => (
                <TableRow key={dep.id}>
                  <TableCell>
                    {dep.first_name} {dep.last_name}
                  </TableCell>
                  <TableCell>{dep.gender}</TableCell>
                  <TableCell>{dep.dob}</TableCell>
                  <TableCell>
                    <span
                      className={`${classes.statusBadge} ${
                        dep.status === "approved"
                          ? classes.approvedStatus
                          : dep.status === "rejected"
                          ? classes.declinedStatus
                          : classes.pending
                      }`}
                    >
                      {dep.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {dep.status === "pending" ? (
                      <>
                        <Button
                          disabled={updatingId === dep.id}
                          className={classes.approved}
                          onClick={() => handleStatusChange(dep.id, "approved")}
                        >
                          {updatingId === dep.id ? "Approving..." : "Approve"}
                        </Button>
                        &nbsp;
                        <Button
                          disabled={updatingId === dep.id}
                          className={classes.declined}
                          onClick={() => handleStatusChange(dep.id, "rejected")}
                        >
                          {updatingId === dep.id ? "Declining..." : "Decline"}
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No actions available
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default EnrolleeDependent;
